import { supabase } from './serviceService.js';
import { optimizeImageForUpload, validateImageFile } from './imageOptimizationService.js';

// About/hero text is stored in Supabase; cache in-memory per session.
const TABLE = 'about_content';
let aboutContentCache = null;
let aboutInFlightPromise = null;
const ABOUT_CACHE_KEY = 'rb_about_cache_v2';
const ABOUT_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const DEFAULT_ABOUT_CONTENT = {
  hero: { eyebrow: '', title: '', lead: '', meta: [] },
  portrait: { chip: '', quote: '', src: '', showImage: false },
  stats: [],
  timeline: [
    { title: 'Första möte', body: 'Vi kartlägger nuläget och dina behov.' },
    { title: 'Plan och upplägg', body: 'Vi sätter en tydlig plan för arbetet framåt.' },
    { title: 'Löpande samarbete', body: 'Vi följer upp, rapporterar och utvecklar tillsammans.' },
  ],
  posts: [],
  postsButton: '',
  cta: { eyebrow: '', heading: '', button: '' },
};

const canUseStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const readAboutStorageCache = () => {
  if (!canUseStorage()) return null;
  try {
    const raw = window.localStorage.getItem(ABOUT_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if ('hero' in parsed || 'timeline' in parsed || 'cta' in parsed) {
      return { content: parsed, isFresh: true };
    }
    const content = parsed.content && typeof parsed.content === 'object' ? parsed.content : null;
    const savedAt = Number(parsed.savedAt || 0);
    if (!content) return null;
    const isFresh = savedAt > 0 ? Date.now() - savedAt < ABOUT_CACHE_MAX_AGE_MS : true;
    return { content, isFresh };
  } catch (_) {
    return null;
  }
};

const writeAboutStorageCache = (content) => {
  if (!canUseStorage() || !content || typeof content !== 'object') return;
  try {
    window.localStorage.setItem(ABOUT_CACHE_KEY, JSON.stringify({
      savedAt: Date.now(),
      content,
    }));
  } catch (_) {
  }
};

const getAboutFallback = () => readAboutStorageCache()?.content || DEFAULT_ABOUT_CONTENT;

const fetchAboutContent = async () => {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('id', 1)
    .maybeSingle();

  if (error) throw error;
  const content = data ? data.content : null;
  if (content) {
    writeAboutStorageCache(content);
  }
  return content;
};

export const aboutService = {
  getCached: () => aboutContentCache,

  get: async ({ force = false } = {}) => {
    if (!force && aboutContentCache) {
      return aboutContentCache;
    }

    if (!force) {
      const storageCache = readAboutStorageCache();
      if (storageCache?.content && storageCache.isFresh) {
        aboutContentCache = storageCache.content;
        return storageCache.content;
      }
    }

    if (!force && aboutInFlightPromise) {
      return aboutInFlightPromise;
    }

    aboutInFlightPromise = fetchAboutContent()
      .then((content) => {
        const resolvedContent = content || getAboutFallback();
        aboutContentCache = resolvedContent;
        return resolvedContent;
      })
      .catch((error) => {
        const fallbackContent = getAboutFallback();
        aboutContentCache = fallbackContent;
        if (fallbackContent) {
          return fallbackContent;
        }
        throw error;
      })
      .finally(() => {
        aboutInFlightPromise = null;
      });

    return aboutInFlightPromise;
  },

  warmup: async () => {
    try {
      await aboutService.get();
    } catch (_) {
    }
  },

  upsert: async (content) => {
    // Single-row table keyed by id=1, so upsert keeps it idempotent.
    const payload = { id: 1, content };
    const { data, error } = await supabase
      .from(TABLE)
      .upsert(payload, { onConflict: 'id' })
      .select()
      .single();

    if (error) throw error;
    aboutContentCache = data.content;
    writeAboutStorageCache(data.content);
    return data.content;
  },

  uploadPortraitImage: async (file) => {
    const validation = validateImageFile(file);
    if (!validation.valid) {
      throw new Error(validation.reason);
    }

    // Store portrait assets in Supabase Storage under images/about/.
    const optimized = await optimizeImageForUpload(file);
    const fileName = `${Date.now()}.${optimized.extension}`;
    const filePath = `about/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('images')
      .upload(filePath, optimized.file, {
        contentType: optimized.mimeType,
        cacheControl: '31536000',
        upsert: false,
      });

    if (uploadError) throw uploadError;

    const { data: publicUrl } = supabase.storage
      .from('images')
      .getPublicUrl(filePath);

    return {
      publicUrl: publicUrl.publicUrl,
      format: optimized.extension.toUpperCase(),
      optimized: optimized.optimized,
    };
  },

  deletePortraitImageByUrl: async (imageUrl) => {
    // Deletes only about/ objects to avoid touching unrelated uploads.
    if (!imageUrl) return;

    const marker = '/images/';
    const markerIndex = imageUrl.indexOf(marker);
    if (markerIndex === -1) return;

    const objectPath = decodeURIComponent(imageUrl.slice(markerIndex + marker.length));
    if (!objectPath || !objectPath.startsWith('about/')) return;

    const { error } = await supabase.storage
      .from('images')
      .remove([objectPath]);

    if (error) throw error;
  },
};
